import { type LuxAiApi as API, type Models as M, Errors as E } from "@lux-ai/api-interface"
import { type ApiModels } from "@lux-ai/api-models"
import { mockLuxAiApi } from "@lux-ai/client"
import { LuxedoRPC } from "luxedo-rpc"

type ErrorDetails = {
	code: number // The error code (usually just maps to an HTTP error code)
	method?: string // the fetch method (if applicable)
	original?: Error // The original error, if this was used to propogate an error status
	location: string // The API route, method or module where this error originated
	response?: Response // the response from where the error originated (if coming from a fetch request)
}

const THEME_DEFAULT_OPTIONS = {
	limit: 100,
}

export class LuxAiApiError extends Error {
	details: ErrorDetails
	code: number

	constructor(
		message: string,
		details: ErrorDetails = {
			code: 9999,
			location: "LuxAiApiError",
		},
	) {
		super(message)

		this.code = details.code
		this.details = details
	}

	static async fromResponse(res: Response, method: string, path: string) {
		let message: string
		try {
			const body = await res.json()
			message = body.message
		} catch (e) {
			message = `An error occurred while sending a ${method} request to ${path} (${res.status}).`
		}
		return new LuxAiApiError(message, {
			code: res.status,
			location: path,
			method: method,
			response: res,
		})
	}
}

export class LuxAiApi implements API {
	apiUrl: string
	requestDefault: Partial<RequestInit>

	constructor() {
		const headers = new Headers()
		headers.append("X-Csrftoken", LuxedoRPC.getCsrfToken())
		headers.append("Content-Type", "application/json")

		this.apiUrl = `${import.meta.env.VITE_API_URL}/api/luxai`
		this.requestDefault = {
			headers,
			credentials: "include",
		}
	}

	async makeRequest(path: string, method: string, options?: RequestInit) {
		const res = await fetch(`${this.apiUrl}${path}`, {
			...this.requestDefault,
			method: method,
			...options,
		})

		if (!res.ok) throw await LuxAiApiError.fromResponse(res, method, path)

		return res.json()
	}

	async fetchTags() {
		const res: ApiModels["Theme"]["tags"] = await this.makeRequest("/theme-tags", "GET")
		return res
	}

	async fetchThemes(options: {
		limit: number
		offset?: number
		category_id: M.Collection["id"]
		free?: boolean
		search?: string
		tags?: Array<string>
	}): Promise<{
		total: number
		limit: number
		offset: number
		themes: M.Theme[]
	}> {
		const { limit, offset, category_id, free, search, tags } = {
			...THEME_DEFAULT_OPTIONS,
			...options,
		}

		let route = `/themes?limit=${limit}`

		if (offset) route = `${route}&offset=${offset}`
		if (category_id) route = `${route}&category_id=${category_id}`
		if (free) route = `${route}&free=${free}`
		if (search) route = `${route}&search=${search}`
		if (tags && tags.length) route = `${route}&tags=${tags.join(",")}`

		const res: ApiModels["ThemeList"] = await this.makeRequest(route, "GET")
		return {
			...res,
			themes: res.themes.map(this.translateApiTheme),
		}
	}

	async fetchActiveGenerations(): Promise<M.ThemeGeneration[]> {
		const queryParams = new URLSearchParams({
			status: "ONGOING",
		})

		const res: ApiModels["LuxAIFlowList"] = await this.makeRequest(`/flows?${queryParams}`, "GET")
		const activeFlows = res["flows"]

		return activeFlows.map(this.translateFlowToGeneration)
	}

	async fetchCollections(): Promise<M.Collection[]> {
		const collections: Array<ApiModels["Category"]> = await this.makeRequest(`/categories`, "GET")
		return collections.map((col) => ({ id: col.id, name: col.name }))
	}

	async fetchGeneratedScenes(generationId?: M.ThemeGeneration["id"]): Promise<M.ThemeScene[]> {
		let url = `/flows`
		if (generationId) url = `${url}/${generationId}`

		try {
			const res: ApiModels["LuxAIFlowList"] = await this.makeRequest(url, "get")

			const allFlows = res.flows
			const themeScenes: M.ThemeScene[] = []

			for (const flow of allFlows) {
				if (!flow.scene) continue
				themeScenes.push({
					theme_id: flow.theme?.id,
					generation_id: flow.id,
					id: flow.scene?.id,
				})
			}

			return themeScenes
		} catch (e) {
			if (!(e instanceof LuxAiApiError)) throw e
			if (e.code === 404) throw new E.MissingError("Failed to get generation data.", e.message)
			if (e.code === 424)
				throw new E.FailedGenerationError(
					"An error occurred while generating your theme.",
					e.message,
				)
			if (e.code === 425)
				throw new E.FailedRenderError("An error occurred while rendering your theme.", e.message)
		}
	}

	async fetchGeneration(id: M.ThemeGeneration["id"]): Promise<M.ThemeGeneration> {
		try {
			const flow = await this.makeRequest(`/flows/${id}`, "get")
			const generation = this.translateFlowToGeneration(flow)

			return generation
		} catch (e) {
			if (!(e instanceof LuxAiApiError)) throw e
			if (e.code === 404) throw new E.MissingError("Failed to get generation data.", e.message)
			if (e.code === 424)
				throw new E.FailedGenerationError(
					"An error occurred while generating your theme.",
					e.message,
				)
			if (e.code === 425)
				throw new E.FailedRenderError("An error occurred while rendering your theme.", e.message)
		}
	}

	async cancelGeneration(id: M.ThemeGeneration["id"]): Promise<void> {
		try {
			return await this.makeRequest(`/flows/${id}/cancel`, "post")
		} catch (e) {
			if (!(e instanceof LuxAiApiError)) throw e
			if (e.code === 404) throw new E.MissingError("Failed to get generation data.", e.message)
			if (e.code === 409)
				throw new E.InvalidStateError("An error occurred while generating your theme.", e.message)
		}
	}

	async completeGeneration(id: M.ThemeGeneration["id"]): Promise<void> {
		try {
			return await this.makeRequest(`/flows/${id}/complete`, "put")
		} catch (e) {
			if (!(e instanceof LuxAiApiError)) throw e
			if (e.code === 404) throw new E.MissingError("Failed to get generation data.", e.message)
			if (e.code === 409)
				throw new E.InvalidStateError("An error occurred while generating your theme.", e.message)
		}
	}

	async fetchGenerationProgress(id: M.ThemeGeneration["id"]): Promise<M.GenerationProgress> {
		try {
			const flow: ApiModels["LuxAIFlow"] = await this.makeRequest(`/flows/${id}`, "get")
			const type = this.parseFlowType(flow)

			const genProgress: M.GenerationProgress = {
				id: flow.id,
				progress: 0,
			}

			// would be type === "preview" first if using that flow
			if (type === "theme") genProgress.progress = flow.themed_video.progress * 0.9
			else genProgress.progress = flow.scene.progress * 0.1 + 0.9

			return genProgress
		} catch (e) {
			if (!(e instanceof LuxAiApiError)) throw e
			if (e.code === 404) throw new E.MissingError("Failed to get generation data.", e.message)
			if (e.code === 424)
				throw new E.FailedGenerationError(
					"An error occurred while generating your theme.",
					e.message,
				)
			if (e.code === 425)
				throw new E.FailedRenderError("An error occurred while rendering your theme.", e.message)
		}
	}

	async generateTheme(request: M.ThemeGenerationRequest): Promise<M.ThemeGeneration> {
		if (request.type === "preview")
			throw Error("Previews are not currently supported, please generate a theme.")

		try {
			const createdFlow: ApiModels["CreatedFlowResponse"] = await this.makeRequest(
				"/flows",
				"post",
				{
					body: JSON.stringify({
						theme_id: request.theme_id,
						snapshot_id: request.snapshot_id,
					}),
				},
			)
			return await this.fetchGeneration(createdFlow.id)
		} catch (e) {
			if (!(e instanceof LuxAiApiError)) throw e
			if (e.code === 403)
				throw new E.NotEnoughTokensError(
					"Your token balance is too low to generate this theme.",
					e.message,
				)
			if (e.code === 404) throw new E.MissingError("Chosen theme is unavailable.", e.message)
			if (e.code === 409)
				throw new E.FreeThemeAlreadyGeneratedError(
					"You have already generated this free theme once. Delete your scene to generate again.",
					e.message,
				)
			if (e.code === 429)
				throw new E.OngoingGenerationsError(
					"Finish or cancel an active generation before starting a new one.",
					e.message,
				)
		}
	}

	private parseFlowType = (aiFlow: ApiModels["LuxAIFlow"]): M.ThemeGeneration["type"] => {
		if (aiFlow.step === "PREVIEW") return "preview"
		else if (aiFlow.scene) return "scene"
		else return "theme"
	}

	private translateFlowToGeneration = (aiFlow: ApiModels["LuxAIFlow"]): M.ThemeGeneration => {
		return {
			id: aiFlow.id,
			snapshot_id: aiFlow.snapshot?.id,
			theme_id: aiFlow.theme?.id,
			type: this.parseFlowType(aiFlow),
			output: aiFlow.themed_video?.url ?? aiFlow.preview?.url,
			scene_id: aiFlow.scene?.id ? Number(aiFlow.scene.id) : null,
		}
	}

	private translateApiTheme = (apiTheme: ApiModels["Theme"]): M.Theme => {
		return {
			id: apiTheme.id,
			description: apiTheme.description,
			name: apiTheme.name,
			thumbnailSrc: apiTheme.thumbnail_img_url,
			videoSrc: apiTheme.thumbnail_video_url,
			tags: apiTheme.tags,
			category: apiTheme.category,
			free: apiTheme.free,
			public: apiTheme.public,
			releaseDate: apiTheme.release_date,
		}
	}
}

export function getLuxAiApi(): LuxAiApi {
	if (import.meta.env.VITE_MOCK_LUX_AI_API) {
		// @ts-ignore
		return mockLuxAiApi
	} else {
		return new LuxAiApi()
	}
}
